/**
 * The shell, assembled.
 *
 * Read the nesting as the answer to "what does a host application actually provide?" —
 * because that list is exactly what is here and nothing else is:
 *
 *   ThemeProvider        the skin styles itself from theme tokens and installs no theme,
 *                        so that it looks native in ICRM, Assets and Accounts. Standalone
 *                        there is no host theme, and `createInventoryTheme()` exists in
 *                        the skin for precisely these two callers — this and a Storybook.
 *   QueryClientProvider  the module never creates a QueryClient; a host has one.
 *   InventoryProvider    the base URL, the token supplier, and the mirrored-master links.
 *   BrowserRouter        the module ships no router, on purpose: three hosts are Next
 *                        app-router and two are react-router 6.
 *   AuthProvider         the session. This is the part with no host analogue at all.
 *
 * The order matters in one place. `AuthProvider` is inside `BrowserRouter` because it
 * needs the current location — the callback route must not be redirected away from — and
 * inside `InventoryProvider` because it subscribes to the transport's 401s.
 */

import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { InventoryProvider } from '@inai/inventory-core/react';
import { createInventoryTheme } from '@inai/inventory-mui';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { HOST_LINKS, inventoryClient } from './api';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/context';
import { CallbackRoute } from './auth/CallbackRoute';
import { CALLBACK_PATH } from './auth/redirect';
import { SHELL_CONFIG } from './config';
import { queryClient } from './queryClient';
import { AppRoutes } from './shell/AppRoutes';
import { FaultNotice } from './shell/FaultNotice';
import { ManualTokenPanel } from './shell/ManualTokenPanel';
import { SessionGate } from './shell/SessionGate';
import { ShellLayout } from './shell/ShellLayout';
import { Splash } from './shell/Splash';

const theme = createInventoryTheme({
  typography: {
    fontFamily:
      '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
});

/** Everything that requires a session. */
function Authenticated() {
  const { status, fault, signIn, signOut } = useAuth();

  if (status === 'booting') return <Splash message="Starting…" />;
  if (status === 'redirecting') return <Splash message="Redirecting to sign in…" />;
  if (status === 'awaiting_token') return <ManualTokenPanel />;
  if (status === 'blocked' && fault) {
    return <FaultNotice fault={fault} onRetry={() => signIn('/items')} onSignOut={signOut} />;
  }

  return (
    <ShellLayout>
      <SessionGate>
        <AppRoutes />
      </SessionGate>
    </ShellLayout>
  );
}

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <InventoryProvider client={inventoryClient} links={HOST_LINKS}>
          <BrowserRouter basename={SHELL_CONFIG.basePath}>
            <AuthProvider>
              <Routes>
                {/* Outside the gate: the callback runs before there is a session. */}
                <Route path={CALLBACK_PATH} element={<CallbackRoute />} />
                <Route path="*" element={<Authenticated />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </InventoryProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
